import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded"
import CancelRoundedIcon from "@mui/icons-material/CancelRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createPayment,
  deletePayment,
  getPaymentById,
  getPaymentFormData,
  getPayments,
  updatePayment,
} from "../../services/paymentService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const statusLabels = {
  confirmed: "Confirmé",
  pending: "En attente",
  cancelled: "Annulé",
}


const statusColors = {
  confirmed: "success",
  pending: "warning",
  cancelled: "error",
}


// =========================================================
// HELPERS
// =========================================================

const buildRequestKey = (prefix) => {
  const randomPart =
    typeof crypto !== "undefined" &&
    crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()
          .toString(16)
          .slice(2)}`

  return `${prefix}-${randomPart}`
}


const getCurrentUserId = () => {
  const value = Number(
    localStorage.getItem("kongosys_user_id") || 0
  )

  return Number.isFinite(value) && value > 0
    ? value
    : 1
}


const createDefaultFormData = () => ({
  company_id: Number(
    localStorage.getItem("kongosys_company_id") || 1
  ),
  sale_id: "",
  payment_date: "",
  amount: "",
  payment_method: "cash",
  payment_reference: "",
  status: "confirmed",
  notes: "",
  external_reference: buildRequestKey("PAYREQ"),
  created_by: getCurrentUserId(),
})


function toNumber(value) {
  const number = Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatAmount(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}


function formatDate(value) {
  if (!value) return "-"

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return "-"
  }

  return date.toLocaleDateString("fr-FR")
}


function formatDateTimeForInput(value) {
  if (!value) return ""

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return ""
  }

  const offset = date.getTimezoneOffset()
  const localDate = new Date(
    date.getTime() - offset * 60000
  )

  return localDate.toISOString().slice(0, 16)
}


function getStatusLabel(value) {
  return statusLabels[value] || value || "-"
}


function getStatusColor(value) {
  return statusColors[value] || "default"
}


function getPaymentRowClass(status) {
  if (status === "confirmed") {
    return "payment-row payment-row--confirmed"
  }

  if (status === "pending") {
    return "payment-row payment-row--pending"
  }

  if (status === "cancelled") {
    return "payment-row payment-row--cancelled"
  }

  return "payment-row payment-row--default"
}


function getErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || JSON.stringify(item))
      .join(" | ")
  }

  if (detail && typeof detail === "object") {
    return detail.message || JSON.stringify(detail)
  }

  return error?.message || fallback
}


// =========================================================
// KPI
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: { xs: 1.35, sm: 1.5 },
        py: 1.15,
        minWidth: 0,
        borderRadius: 3,
        borderColor: "rgba(148, 163, 184, 0.22)",
        bgcolor: "#ffffff",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            borderRadius: 2.5,
            color: accent,
            bgcolor: background,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.7,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 19, sm: 21 },
                lineHeight: 1,
                fontWeight: 900,
                color: "#0f172a",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                fontWeight: 800,
                color: "#334155",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 0.35,
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// MOBILE CARD
// =========================================================

function PaymentMobileCard({
  row,
  paymentMethodLabel,
  onEdit,
  onDelete,
  deletingId,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "rgba(148, 163, 184, 0.22)",
        boxShadow:
          row.status === "confirmed"
            ? "inset 4px 0 0 #22c55e, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : row.status === "pending"
            ? "inset 4px 0 0 #f59e0b, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : row.status === "cancelled"
            ? "inset 4px 0 0 #ef4444, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : "inset 4px 0 0 #cbd5e1, 0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,
          "&:last-child": { pb: 1.5 },
        }}
      >
        <Stack spacing={1.2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={900} color="#0f172a">
                {row.payment_code || `PAY-${row.id}`}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Vente : {row.sale_code || "-"}
              </Typography>
            </Box>

            <Chip
              size="small"
              color={getStatusColor(row.status)}
              label={getStatusLabel(row.status)}
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 1,
              borderRadius: 2.5,
              bgcolor: "#fbfdff",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Client / origine
            </Typography>

            <Typography variant="body2" fontWeight={800} color="#334155">
              {row.customer_name || "Client comptant"}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {[row.store_name, row.organization_name]
                .filter(Boolean)
                .join(" • ") || "-"}
            </Typography>
          </Paper>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 0.8,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 0.9,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Montant reçu
              </Typography>

              <Typography fontWeight={900} color="#0f172a">
                {formatAmount(row.amount)}
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 0.9,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Mode
              </Typography>

              <Typography variant="body2" fontWeight={800}>
                {paymentMethodLabel(row.payment_method)}
              </Typography>
            </Paper>
          </Box>

          <Typography variant="caption" color="text.secondary">
            {formatDate(row.payment_date)}
            {row.payment_reference
              ? ` • ${row.payment_reference}`
              : ""}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 0.7,
            }}
          >
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() => onEdit(row.id)}
              disabled={Boolean(deletingId)}
            >
              Modifier
            </Button>

            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => onDelete(row.id)}
              disabled={Boolean(deletingId)}
            >
              {deletingId === row.id
                ? "Suppression..."
                : "Supprimer"}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}


// =========================================================
// PAGE
// =========================================================

function PaymentsPage() {
  const theme = useTheme()

  const isMdDown = useMediaQuery(
    theme.breakpoints.down("md")
  )

  const isSmDown = useMediaQuery(
    theme.breakpoints.down("sm")
  )

  const [payments, setPayments] = useState([])
  const [sales, setSales] = useState([])
  const [paymentMethods, setPaymentMethods] = useState([])
  const [statuses, setStatuses] = useState([])

  const [loading, setLoading] = useState(false)
  const [loadingFormData, setLoadingFormData] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const [open, setOpen] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)

  const [formData, setFormData] = useState(
    createDefaultFormData
  )

  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [methodFilter, setMethodFilter] = useState("")

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: ROWS_PER_PAGE,
  })

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message: String(message || ""),
    })
  }


  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }


  const paymentMethodLabel = (value) => {
    return (
      paymentMethods.find(
        (item) => item.value === value
      )?.label ||
      value ||
      "-"
    )
  }


  const loadPayments = async () => {
    try {
      setLoading(true)

      const data = await getPayments()

      setPayments(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (error) {
      console.error(
        "Erreur chargement paiements clients :",
        error
      )

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible de charger les paiements clients."
        ),
        "error"
      )
    } finally {
      setLoading(false)
    }
  }


  const loadFormData = async (
    includeSaleId = null
  ) => {
    try {
      setLoadingFormData(true)

      const data = await getPaymentFormData(
        includeSaleId
      )

      setSales(
        Array.isArray(data?.sales)
          ? data.sales
          : []
      )

      setPaymentMethods(
        Array.isArray(data?.payment_methods)
          ? data.payment_methods
          : []
      )

      setStatuses(
        Array.isArray(data?.statuses)
          ? data.statuses
          : []
      )

      return data
    } catch (error) {
      console.error(
        "Erreur chargement formulaire paiement :",
        error
      )

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible de charger les ventes à encaisser."
        ),
        "error"
      )

      return null
    } finally {
      setLoadingFormData(false)
    }
  }


  useEffect(() => {
    loadPayments()
    loadFormData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const resetForm = () => {
    setSelectedPayment(null)
    setFormData(createDefaultFormData())
  }


  const openAddDialog = async () => {
    resetForm()

    await loadFormData()

    setOpen(true)
  }


  const openEditDialog = async (paymentId) => {
    try {
      setLoadingFormData(true)

      const payment = await getPaymentById(
        paymentId
      )

      await loadFormData(
        payment.sale_id
      )

      setSelectedPayment(payment)

      setFormData({
        company_id: payment.company_id ?? 1,
        sale_id: payment.sale_id ?? "",

        payment_date: formatDateTimeForInput(
          payment.payment_date
        ),

        amount:
          payment.amount !== null &&
          payment.amount !== undefined
            ? Number(payment.amount)
            : "",

        payment_method:
          payment.payment_method || "cash",

        payment_reference:
          payment.payment_reference || "",

        status:
          payment.status || "confirmed",

        notes:
          payment.notes || "",

        external_reference:
          payment.external_reference ||
          buildRequestKey("PAYREQ"),

        created_by:
          payment.created_by ??
          getCurrentUserId(),
      })

      setOpen(true)
    } catch (error) {
      console.error(
        "Erreur ouverture paiement :",
        error
      )

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible de charger le paiement sélectionné."
        ),
        "error"
      )
    } finally {
      setLoadingFormData(false)
    }
  }


  const handleClose = () => {
    if (saving) return

    setOpen(false)
    resetForm()
  }


  const selectedSale = useMemo(() => {
    return (
      sales.find(
        (sale) =>
          String(sale.id) ===
          String(formData.sale_id || "")
      ) || null
    )
  }, [sales, formData.sale_id])


  const maxPayableAmount = useMemo(() => {
    if (!selectedSale) return 0

    const remaining = Number(
      selectedSale.remaining_amount || 0
    )

    if (
      selectedPayment &&
      String(selectedPayment.sale_id) ===
        String(selectedSale.id) &&
      selectedPayment.status === "confirmed"
    ) {
      return (
        remaining +
        Number(selectedPayment.amount || 0)
      )
    }

    return remaining
  }, [selectedSale, selectedPayment])


  const handleSaleChange = (event) => {
    const value = event.target.value

    const sale = sales.find(
      (item) =>
        String(item.id) ===
        String(value)
    )

    setFormData((prev) => ({
      ...prev,

      sale_id:
        value === ""
          ? ""
          : Number(value),

      amount:
        sale
          ? Number(
              sale.remaining_amount || 0
            )
          : "",
    }))
  }


  const handleChange = (event) => {
    const { name, value } = event.target

    setFormData((prev) => ({
      ...prev,

      [name]:
        name === "amount"
          ? value === ""
            ? ""
            : Number(value)
          : value,
    }))
  }


  const validateForm = () => {
    if (!formData.sale_id) {
      showSnackbar(
        "Sélectionnez une vente à encaisser.",
        "warning"
      )
      return false
    }

    const amount = Number(
      formData.amount || 0
    )

    if (amount <= 0) {
      showSnackbar(
        "Le montant doit être supérieur à zéro.",
        "warning"
      )
      return false
    }

    if (!formData.payment_method) {
      showSnackbar(
        "Sélectionnez un mode de paiement.",
        "warning"
      )
      return false
    }

    if (
      formData.status === "confirmed" &&
      amount > maxPayableAmount + 0.005
    ) {
      showSnackbar(
        `Le montant dépasse le reste disponible de ${formatAmount(
          maxPayableAmount
        )}.`,
        "warning"
      )
      return false
    }

    return true
  }


  const handleSave = async () => {
    if (
      saving ||
      !validateForm()
    ) {
      return
    }

    const payload = {
      company_id: Number(
        formData.company_id || 1
      ),

      sale_id: Number(
        formData.sale_id
      ),

      customer_id:
        selectedSale?.customer_id
          ? Number(selectedSale.customer_id)
          : null,

      store_id:
        selectedSale?.store_id
          ? Number(selectedSale.store_id)
          : null,

      payment_date:
        formData.payment_date || null,

      amount: Number(
        formData.amount
      ),

      payment_method:
        formData.payment_method,

      payment_reference:
        formData.payment_reference?.trim() ||
        null,

      status:
        formData.status || "confirmed",

      notes:
        formData.notes?.trim() ||
        null,

      external_reference:
        formData.external_reference,

      created_by:
        getCurrentUserId(),
    }

    try {
      setSaving(true)

      if (selectedPayment?.id) {
        await updatePayment(
          selectedPayment.id,
          payload
        )

        showSnackbar(
          "Paiement client modifié avec succès."
        )
      } else {
        await createPayment(payload)

        showSnackbar(
          "Paiement client enregistré avec succès."
        )
      }

      setOpen(false)
      resetForm()

      await Promise.all([
        loadPayments(),
        loadFormData(),
      ])
    } catch (error) {
      console.error(
        "Erreur enregistrement paiement :",
        error
      )

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible d'enregistrer le paiement client."
        ),
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  const handleDelete = async (paymentId) => {
    if (deletingId) return

    const confirmed = window.confirm(
      "Supprimer ce paiement client ? " +
        "Le solde de la vente sera recalculé automatiquement."
    )

    if (!confirmed) return

    try {
      setDeletingId(paymentId)

      await deletePayment(paymentId)

      showSnackbar(
        "Paiement client supprimé avec succès."
      )

      await Promise.all([
        loadPayments(),
        loadFormData(),
      ])
    } catch (error) {
      console.error(
        "Erreur suppression paiement :",
        error
      )

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible de supprimer le paiement client."
        ),
        "error"
      )
    } finally {
      setDeletingId(null)
    }
  }


  const filteredPayments = useMemo(() => {
    const keyword = search
      .trim()
      .toLowerCase()

    return payments.filter((item) => {
      const matchesSearch =
        keyword === "" ||
        [
          item.payment_code,
          item.sale_code,
          item.customer_name,
          item.store_name,
          item.organization_name,
          item.payment_reference,
          item.payment_method,
          paymentMethodLabel(item.payment_method),
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(keyword)
        )

      const matchesStatus =
        statusFilter === "" ||
        String(item.status || "") === statusFilter

      const matchesMethod =
        methodFilter === "" ||
        String(item.payment_method || "") === methodFilter

      return (
        matchesSearch &&
        matchesStatus &&
        matchesMethod
      )
    })
  }, [
    payments,
    search,
    statusFilter,
    methodFilter,
    paymentMethods,
  ])


  const summary = useMemo(() => {
    return filteredPayments.reduce(
      (accumulator, item) => {
        accumulator.count += 1

        const amount = toNumber(item.amount)

        if (item.status === "confirmed") {
          accumulator.confirmed += amount
        }

        if (item.status === "pending") {
          accumulator.pending += amount
        }

        if (item.status === "cancelled") {
          accumulator.cancelled += amount
        }

        return accumulator
      },
      {
        count: 0,
        confirmed: 0,
        pending: 0,
        cancelled: 0,
      }
    )
  }, [filteredPayments])


  useEffect(() => {
    setPaginationModel((previous) => ({
      ...previous,
      page: 0,
      pageSize: ROWS_PER_PAGE,
    }))
  }, [search, statusFilter, methodFilter])


  const paginatedMobileRows = useMemo(() => {
    const start =
      paginationModel.page * ROWS_PER_PAGE

    return filteredPayments.slice(
      start,
      start + ROWS_PER_PAGE
    )
  }, [
    filteredPayments,
    paginationModel.page,
  ])


  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(
        filteredPayments.length / ROWS_PER_PAGE
      )
    )

    if (paginationModel.page >= totalPages) {
      setPaginationModel((previous) => ({
        ...previous,
        page: totalPages - 1,
        pageSize: ROWS_PER_PAGE,
      }))
    }
  }, [
    filteredPayments.length,
    paginationModel.page,
  ])


  const resetFilters = () => {
    setSearch("")
    setMethodFilter("")
    setStatusFilter("")

    setPaginationModel({
      page: 0,
      pageSize: ROWS_PER_PAGE,
    })
  }


  const columns = useMemo(
    () => [
      {
        field: "payment",
        headerName: "Paiement",
        minWidth: 190,
        flex: 1.05,
        sortable: false,
        renderCell: (params) => (
          <Stack
            spacing={0.16}
            justifyContent="center"
            sx={{
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: "0.86rem",
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.payment_code || `PAY-${params.row.id}`}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700 }}
            >
              {formatDate(params.row.payment_date)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "sale",
        headerName: "Vente / client",
        minWidth: 230,
        flex: 1.25,
        sortable: false,
        renderCell: (params) => (
          <Stack
            spacing={0.14}
            justifyContent="center"
            sx={{
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                color: "#334155",
                fontSize: "0.83rem",
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.sale_code || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
            >
              {params.row.customer_name || "Client comptant"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "origin",
        headerName: "Shop / organisation",
        minWidth: 220,
        flex: 1.2,
        sortable: false,
        renderCell: (params) => (
          <Stack
            spacing={0.14}
            justifyContent="center"
            sx={{
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                color: "#334155",
                fontSize: "0.83rem",
                fontWeight: 800,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.store_name || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
            >
              {params.row.organization_name || "Organisation non renseignée"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "amount",
        headerName: "Montant",
        width: 125,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography
            sx={{
              width: "100%",
              textAlign: "right",
              color: "#0f172a",
              fontSize: "0.87rem",
              fontWeight: 900,
            }}
          >
            {formatAmount(params.value)}
          </Typography>
        ),
      },
      {
        field: "payment_method",
        headerName: "Mode",
        width: 135,
        renderCell: (params) => (
          <Chip
            label={paymentMethodLabel(params.value)}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              maxWidth: 125,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        ),
      },
      {
        field: "status",
        headerName: "Statut",
        width: 120,
        renderCell: (params) => (
          <Chip
            label={getStatusLabel(params.value)}
            size="small"
            color={getStatusColor(params.value)}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 190,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.55}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={() =>
                openEditDialog(params.row.id)
              }
              disabled={Boolean(deletingId)}
            >
              Modifier
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() =>
                handleDelete(params.row.id)
              }
              disabled={Boolean(deletingId)}
            >
              {deletingId === params.row.id
                ? "..."
                : "Supprimer"}
            </Button>
          </Stack>
        ),
      },
    ],
    [
      deletingId,
      paymentMethods,
    ]
  )


  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          pb: { xs: 2, md: 4 },

          "& .MuiButton-root": {
            minHeight: 40,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 800,
            boxShadow: "none",
          },

          "& .MuiOutlinedInput-root": {
            borderRadius: 2.5,
            bgcolor: "#ffffff",
          },

          "& .MuiChip-root": {
            fontWeight: 800,
          },

          "& .MuiPaper-root": {
            backgroundImage: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "minmax(0, 1fr) auto",
            },
            gap: 1.5,
            alignItems: "start",
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: "#0f172a",
                lineHeight: 1.12,
                letterSpacing: "-0.025em",
                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Paiements clients
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.4,
                lineHeight: 1.5,
                maxWidth: 800,
              }}
            >
              Encaissements reçus des clients pour régler leurs ventes.
            </Typography>
          </Box>

          <Button
            variant="contained"
            disableElevation
            onClick={openAddDialog}
            disabled={
              loadingFormData ||
              sales.length === 0
            }
            sx={{
              minWidth: { xs: "100%", sm: 220 },
              justifySelf: { xs: "stretch", sm: "end" },
            }}
          >
            + Ajouter un paiement
          </Button>
        </Box>

        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mb: 1.6,
            borderRadius: 3,
            py: 0.35,
          }}
        >
          Cette page enregistre une{" "}
          <strong>entrée d&apos;argent liée à une vente</strong>.
          Une vente totalement payée disparaît automatiquement du formulaire.
          Le versement du shop vers la caisse centrale doit être enregistré
          dans <strong>Dépôts caisse</strong>, pas une deuxième fois ici.
        </Alert>

        {sales.length === 0 &&
        !loadingFormData && (
          <Alert
            severity="success"
            variant="outlined"
            sx={{
              mb: 1.6,
              borderRadius: 3,
            }}
          >
            Aucune vente ne possède actuellement un reste à payer.
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.1,
            mb: 1.6,
          }}
        >
          <MetricCard
            label="Paiements"
            value={summary.count}
            caption="Paiements affichés"
            accent="#2563eb"
            background="rgba(37, 99, 235, 0.09)"
            icon={<PaymentsRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="Confirmés"
            value={formatAmount(summary.confirmed)}
            caption="Montant encaissé confirmé"
            accent="#16a34a"
            background="rgba(22, 163, 74, 0.09)"
            icon={<CheckCircleRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="En attente"
            value={formatAmount(summary.pending)}
            caption="Montant à confirmer"
            accent="#d97706"
            background="rgba(217, 119, 6, 0.09)"
            icon={<ScheduleRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="Annulés"
            value={formatAmount(summary.cancelled)}
            caption="Montant annulé"
            accent="#dc2626"
            background="rgba(220, 38, 38, 0.09)"
            icon={<CancelRoundedIcon fontSize="small" />}
          />
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.15, sm: 1.25 },
            mb: 1.6,
            borderRadius: 3,
            borderColor: "rgba(148, 163, 184, 0.22)",
            boxShadow: "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "minmax(0, 1fr) 210px 210px 135px",
              },
              gap: 1,
              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Recherche"
              placeholder="Code, vente, client, shop, organisation..."
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
            />

            <TextField
              select
              fullWidth
              size="small"
              label="Mode"
              value={methodFilter}
              onChange={(event) =>
                setMethodFilter(event.target.value)
              }
            >
              <MenuItem value="">Tous</MenuItem>

              {paymentMethods.map((method) => (
                <MenuItem
                  key={method.value}
                  value={method.value}
                >
                  {method.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Statut"
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value)
              }
            >
              <MenuItem value="">Tous</MenuItem>

              {statuses.map((status) => (
                <MenuItem
                  key={status.value}
                  value={status.value}
                >
                  {status.label}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              onClick={resetFilters}
              sx={{
                width: "100%",
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3.5,
            overflow: "hidden",
            borderColor: "rgba(148, 163, 184, 0.22)",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: { xs: 1.4, sm: 1.8 },
              py: 1.15,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 0.8,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography fontWeight={900} color="#0f172a">
                Registre des paiements clients
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Vente, client, origine, montant, mode et statut
              </Typography>
            </Box>

            <Chip
              size="small"
              variant="outlined"
              label={`${filteredPayments.length} paiement${
                filteredPayments.length > 1 ? "s" : ""
              }`}
            />
          </Box>

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                overflowX: "auto",
                bgcolor: "#ffffff",
              }}
            >
              <Box sx={{ minWidth: 1080 }}>
                <DataGrid
                  autoHeight
                  rows={filteredPayments}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  disableColumnSelector
                  pagination
                  paginationMode="client"
                  paginationModel={paginationModel}
                  onPaginationModelChange={(model) =>
                    setPaginationModel({
                      page: model.page,
                      pageSize: ROWS_PER_PAGE,
                    })
                  }
                  pageSizeOptions={[ROWS_PER_PAGE]}
                  rowHeight={72}
                  columnHeaderHeight={50}
                  getRowClassName={(params) =>
                    getPaymentRowClass(params.row.status)
                  }
                  localeText={{
                    noRowsLabel: "Aucun paiement",
                    noResultsOverlayLabel: "Aucun résultat",
                    MuiTablePagination: {
                      labelRowsPerPage: "",
                      labelDisplayedRows: ({
                        from,
                        to,
                        count,
                      }) => `${from}–${to} sur ${count}`,
                    },
                  }}
                  sx={{
                    border: 0,
                    width: "100%",
                    color: "#334155",
                    fontSize: "0.83rem",
                    bgcolor: "#ffffff",

                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #dfe7f1",
                    },

                    "& .MuiDataGrid-columnHeader": {
                      bgcolor: "#f8fafc",
                      px: 1.25,
                      borderRight:
                        "1px solid rgba(226, 232, 240, 0.60)",
                    },

                    "& .MuiDataGrid-columnHeaderTitle": {
                      color: "#334155",
                      fontSize: "0.76rem",
                      fontWeight: 900,
                      letterSpacing: "0.025em",
                    },

                    "& .MuiDataGrid-cell": {
                      px: 1.25,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      overflow: "hidden",
                      borderBottom: "1px solid #edf2f7",
                      borderRight:
                        "1px solid rgba(241, 245, 249, 0.85)",
                    },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline: "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline: "none",
                      },

                    "& .payment-row": {
                      transition:
                        "background-color 0.16s ease, box-shadow 0.16s ease",
                    },

                    "& .payment-row--confirmed": {
                      boxShadow: "inset 4px 0 0 #22c55e",
                      bgcolor: "rgba(240, 253, 244, 0.30)",
                    },

                    "& .payment-row--pending": {
                      boxShadow: "inset 4px 0 0 #f59e0b",
                      bgcolor: "rgba(255, 251, 235, 0.34)",
                    },

                    "& .payment-row--cancelled": {
                      boxShadow: "inset 4px 0 0 #ef4444",
                      bgcolor: "rgba(254, 242, 242, 0.30)",
                    },

                    "& .payment-row--default": {
                      boxShadow: "inset 4px 0 0 #cbd5e1",
                    },

                    "& .payment-row:hover": {
                      bgcolor: "#f8fbff !important",
                      boxShadow:
                        "inset 4px 0 0 #2563eb, 0 4px 14px rgba(15, 23, 42, 0.045)",
                    },

                    "& .MuiDataGrid-footerContainer": {
                      minHeight: 52,
                      bgcolor: "#fbfdff",
                      borderTop: "1px solid #dfe7f1",
                    },

                    "& .MuiTablePagination-toolbar": {
                      minHeight: 52,
                      px: 1.25,
                    },

                    "& .MuiTablePagination-selectLabel": {
                      display: "none",
                    },

                    "& .MuiTablePagination-input": {
                      display: "none",
                    },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: { xs: 1.1, sm: 1.25 },
                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    Chargement des paiements...
                  </Typography>
                </Box>
              ) : paginatedMobileRows.length === 0 ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    Aucun paiement trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedMobileRows.map((row) => (
                    <PaymentMobileCard
                      key={row.id}
                      row={row}
                      paymentMethodLabel={paymentMethodLabel}
                      onEdit={openEditDialog}
                      onDelete={handleDelete}
                      deletingId={deletingId}
                    />
                  ))}
                </Stack>
              )}

              <TablePagination
                component="div"
                count={filteredPayments.length}
                page={paginationModel.page}
                onPageChange={(_event, newPage) =>
                  setPaginationModel((previous) => ({
                    ...previous,
                    page: newPage,
                    pageSize: ROWS_PER_PAGE,
                  }))
                }
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                labelRowsPerPage=""
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} sur ${count}`
                }
                sx={{
                  mt: 1,
                  bgcolor: "#ffffff",
                  borderRadius: 2.5,

                  "& .MuiTablePagination-selectLabel": {
                    display: "none",
                  },

                  "& .MuiTablePagination-input": {
                    display: "none",
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: "100dvh", sm: "92dvh" },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.3 }}>
          <Typography fontWeight={900} fontSize={20} color="#0f172a">
            {selectedPayment
              ? "Modifier le paiement client"
              : "Ajouter un paiement client"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.3 }}
          >
            Enregistrez l’encaissement d’une vente encore partiellement ou
            totalement impayée.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="info" variant="outlined">
              Seules les ventes ayant encore un reste à payer sont proposées.
              Le système bloque les doubles clics, les doublons et les montants
              supérieurs au solde restant.
            </Alert>

            {loadingFormData ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    borderColor: "rgba(148, 163, 184, 0.22)",
                  }}
                >
                  <Typography
                    fontWeight={900}
                    color="#0f172a"
                    sx={{ mb: 1.2 }}
                  >
                    Vente à encaisser
                  </Typography>

                  <TextField
                    select
                    size="small"
                    label="Vente à encaisser"
                    name="sale_id"
                    value={formData.sale_id}
                    onChange={handleSaleChange}
                    fullWidth
                    disabled={
                      saving ||
                      Boolean(selectedPayment)
                    }
                    helperText={
                      selectedPayment
                        ? "La vente ne peut pas être changée pendant une modification."
                        : "Les ventes déjà totalement payées ne sont pas affichées."
                    }
                  >
                    {sales.map((sale) => (
                      <MenuItem
                        key={sale.id}
                        value={sale.id}
                      >
                        {sale.sale_code} |{" "}
                        {String(sale.sale_code || "").startsWith("ORD-")
                          ? "Demande agent"
                          : "Vente"}{" "}
                        | {sale.store_code || "Point"} -{" "}
                        {sale.store_name || "-"} | Reste :{" "}
                        {formatAmount(sale.remaining_amount)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    bgcolor: "#f8fafc",
                    borderColor: "rgba(148, 163, 184, 0.22)",
                  }}
                >
                  <Typography
                    fontWeight={900}
                    color="#0f172a"
                    sx={{ mb: 1.2 }}
                  >
                    Situation de la vente
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 1.5,
                    }}
                  >
                    <TextField
                      size="small"
                      label="Client"
                      value={
                        selectedSale?.customer_name ||
                        "Client comptant / non renseigné"
                      }
                      fullWidth
                      disabled
                    />

                    <TextField
                      size="small"
                      label="Shop"
                      value={
                        selectedSale
                          ? `${
                              selectedSale.store_code || ""
                            } ${
                              selectedSale.store_name || ""
                            }`.trim()
                          : ""
                      }
                      fullWidth
                      disabled
                    />

                    <TextField
                      size="small"
                      label="Reste disponible"
                      value={
                        selectedSale
                          ? formatAmount(maxPayableAmount)
                          : "0,00"
                      }
                      fullWidth
                      disabled
                    />

                    <TextField
                      size="small"
                      label="Montant reçu"
                      name="amount"
                      type="number"
                      value={formData.amount}
                      onChange={handleChange}
                      fullWidth
                      disabled={
                        !selectedSale ||
                        saving
                      }
                      slotProps={{
                        htmlInput: {
                          min: 0,
                          max: maxPayableAmount,
                          step: 0.01,
                        },
                      }}
                    />
                  </Box>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    borderColor: "rgba(148, 163, 184, 0.22)",
                  }}
                >
                  <Typography
                    fontWeight={900}
                    color="#0f172a"
                    sx={{ mb: 1.2 }}
                  >
                    Paiement
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 1.5,
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label="Mode de paiement"
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleChange}
                      fullWidth
                      disabled={saving}
                    >
                      {paymentMethods.map((method) => (
                        <MenuItem
                          key={method.value}
                          value={method.value}
                        >
                          {method.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      size="small"
                      label="Statut"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      fullWidth
                      disabled={saving}
                    >
                      {statuses.map((status) => (
                        <MenuItem
                          key={status.value}
                          value={status.value}
                        >
                          {status.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      type="datetime-local"
                      size="small"
                      label="Date du paiement"
                      name="payment_date"
                      value={formData.payment_date}
                      onChange={handleChange}
                      fullWidth
                      disabled={saving}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      size="small"
                      label="Référence du paiement"
                      name="payment_reference"
                      value={formData.payment_reference}
                      onChange={handleChange}
                      fullWidth
                      disabled={saving}
                      placeholder="Reçu, transaction bancaire, référence mobile..."
                    />
                  </Box>
                </Paper>

                <TextField
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={3}
                  disabled={saving}
                />
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={saving}
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={
              saving ||
              loadingFormData ||
              !selectedSale ||
              !formData.amount
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedPayment
              ? "Mettre à jour"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}

export default PaymentsPage
